<template>
    <b-modal id="send-to-tc-modal" title="Send Summary to Travel Consultant" size="lg" hide-footer>
        <loading-screen :is-loading="isSaving"></loading-screen>
        <errors :error-list="serverErrors"></errors>

        <!-- Description -->
        <div class="card shadow-none card-action send-to-tc-card">
            <!-- To field -->
            <div class="card-menubar card-menubar-top py-1 d-flex align-items-center">
                <label class="p-0 m-0" for="toField"><b>To:</b></label>
                <b-form-tags remove-on-delete tag-pills input-id="toField" v-model="toAddresses" class="border-0"
                    placeholder="Add email address..." add-on-change duplicate-tag-text="Duplicate email(s)"></b-form-tags>
                <div class="flex-shrink-1">
                    <a href="#" @click="showCcField" v-if="!showCc" class="mr-3">Cc</a>
                    <a href="#" @click="showBccField" v-if="!showBcc">Bcc</a>
                </div>
            </div>
            <!-- / To field -->
            <!-- CC field -->
            <div v-if="showCc" class="card-menubar card-menubar-top py-1 d-flex align-items-center">
                <label class="p-0 m-0" for="ccField"><b>Cc:</b></label>
                <b-form-tags remove-on-delete tag-pills input-id="ccField" v-model="ccAddresses" class="border-0"
                    placeholder="Add email address..." add-on-change duplicate-tag-text="Duplicate email(s)"></b-form-tags>
            </div>
            <!-- / CC field -->
            <!-- BCC field -->
            <div v-if="showBcc" class="card-menubar card-menubar-top py-1 d-flex align-items-center">
                <label class="p-0 m-0" for="bccField"><b>Bcc:</b></label>
                <b-form-tags remove-on-delete tag-pills input-id="bccField" v-model="bccAddresses" class="border-0"
                    placeholder="Add email address..." add-on-change duplicate-tag-text="Duplicate email(s)"></b-form-tags>
            </div>
            <!-- / BCC field -->

            <!-- Email signature not loaded -->
            <div v-if="isLoadingEmailSignature" class="card-body text-center">
                <b-spinner class="m-2" variant="itg-dark" role="status"></b-spinner>
            </div>
            <!-- / Email signature not loaded -->

            <!-- Email signature loaded - show editor -->
            <div v-if="!isLoadingEmailSignature" class="card-body pb-0">
                <editor-content :editor="editor" />
            </div>
            <div v-if="!isLoadingEmailSignature" class="card-menubar">
                <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
                    <div class="menubar">
                        <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                            :class="{ 'is-active': isActive.bold() }" @click="commands.bold">
                            <i class="fas fa-bold"></i>
                        </a>

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                            :class="{ 'is-active': isActive.italic() }" @click="commands.italic">
                            <i class="fas fa-italic"></i>
                        </a>

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                            :class="{ 'is-active': isActive.strike() }" @click="commands.strike">
                            <i class="fas fa-strikethrough"></i>
                        </a>

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                            :class="{ 'is-active': isActive.underline() }" @click="commands.underline">
                            <i class="fas fa-underline"></i>
                        </a>

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                            :class="{ 'is-active': isActive.bullet_list() }" @click="commands.bullet_list">
                            <i class="fas fa-list-ul"></i>
                        </a>

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button"
                            :class="{ 'is-active': isActive.ordered_list() }" @click="commands.ordered_list">
                            <i class="fas fa-list-ol"></i>
                        </a>

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button" @click="commands.undo">
                            <i class="fas fa-undo"></i>
                        </a>

                        <a href="#" class="btn btn-itg-white btn-sm menubar__button" @click="commands.redo">
                            <i class="fas fa-redo"></i>
                        </a>

                        <a @click.prevent="openMediaUploader" href="#" class="btn btn-itg-white btn-sm menubar__button">
                            <i class="fas fa-images"></i>
                        </a>
                        <upload-media-modal @file-uploaded="(r) => handleFileUploaded(r, commands.image)"
                            :ticket-id="ticket.id" modal-id="send-to-tc-upload-media-modal"></upload-media-modal>
                    </div>
                </editor-menu-bar>
            </div>
            <!-- / Email signature loaded - show editor -->

            <div class="card-footer text-right">
                <button type="button" class="btn btn-itg" @click="attemptCancel">
                    Cancel
                </button>

                <button v-if="!isLoadingEmailSignature" type="button" class="btn btn-itg-dark ml-2" @click="forwardTicket">
                    Send
                </button>
            </div>
        </div>
        <!-- / Description -->

    </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Forms from '../../../../../mixins/Forms';
import axios from 'axios';
import UploadMediaModal from './UploadMediaModal.vue';

import { Editor, EditorContent, EditorMenuBar } from "tiptap";
import {
    Blockquote,
    CodeBlock,
    HardBreak,
    Heading,
    OrderedList,
    BulletList,
    ListItem,
    TodoItem,
    TodoList,
    Bold,
    Code,
    Italic,
    Link,
    Strike,
    Underline,
    History,
    Image
} from "tiptap-extensions";

export default {
    data() {
        return {
            isLoadingEmailSignature: true,
            emailSignature: null,

            isSaving: false,
            serverErrors: [],
            editor: null,
            toAddresses: [],

            showCc: false,
            ccAddresses: [],

            showBcc: false,
            bccAddresses: [],
        }
    },

    computed: {
        ...mapGetters("ticketViewer", {
            ticket: "ticket",
            axumData: 'axumData'
        }),

        emailSignatureWithVariablesReplaced() {
            let newSignature = this.emailSignature;

            let agentName = this.$auth.user.name ? this.$auth.user.name : '-';
            newSignature = newSignature.replace('{agentName}', agentName);

            let axumBookingId = this.ticket.axum_booking_id ? this.ticket.axum_booking_id : '-'
            newSignature = newSignature.replace('{axumBookingId}', axumBookingId);

            let ticketNumber = this.ticket.id ? this.ticket.id : '-'
            newSignature = newSignature.replace('{ticketNumber}', ticketNumber);

            return newSignature;
        }
    },

    mounted() {
        this.loadEmailSignatureAndSetupEditor();
    },

    components: { EditorMenuBar, EditorContent, UploadMediaModal },

    mixins: [Forms],

    methods: {
        ...mapActions('ticketViewer', {
            loadMedia: 'loadMedia'
        }),

        attemptCancel() {
            this.$bvModal.hide("send-to-tc-modal");
        },

        loadEmailSignatureAndSetupEditor() {
            let vm = this;
            vm.isLoadingEmailSignature = true;
            vm.serverErrors = [];
            axios
                .get(API_BASE + "/users/current-user/email-signature")
                .then((r) => {
                    vm.isLoadingEmailSignature = false;
                    vm.emailSignature = r.data;
                    vm.setupEditor();
                })
                .catch((e) => {
                    vm.isLoadingEmailSignature = false;
                    vm.emailSignature = null;
                    vm.setErrors(e);
                    vm.setupEditor();
                });
        },

        forwardTicket() {
            let vm = this;
            vm.isSaving = true;
            vm.serverErrors = [];
            axios
                .post(API_BASE + "/tickets/" + this.ticket.id + "/forwarding", {
                    to: this.toAddresses,
                    cc: this.ccAddresses,
                    bcc: this.bccAddresses,
                    body: vm.editor.getHTML(),
                })
                .then((r) => {
                    vm.isSaving = false;
                    this.$notify.success({
                        title: "Success!",
                        message: "Ticket sent to TC successfully",
                    });
                    vm.$bvModal.hide("send-to-tc-modal");
                    // this.actionComplete();
                })
                .catch((e) => {
                    vm.isSaving = false;
                    vm.setErrors(e);
                });
        },

        setupEditor() {
            let vm = this;
            this.editor = new Editor({
                extensions: [
                    new Blockquote(),
                    new CodeBlock(),
                    new HardBreak(),
                    new Heading({ levels: [1, 2, 3] }),
                    new BulletList(),
                    new OrderedList(),
                    new ListItem(),
                    new TodoItem(),
                    new TodoList(),
                    new Bold(),
                    new Code(),
                    new Italic(),
                    new Link(),
                    new Strike(),
                    new Underline(),
                    new History(),
                    new Image()
                ],
                content: this.getDefaultEmailContent(),
                autoFocus: false,
                onInit: function () {
                    vm.$nextTick(function () {
                        window.setTimeout(function () {
                            let container = document.getElementById(
                                "ticket-main-section-left-column"
                            );
                            container.scrollTo({
                                top: container.scrollHeight,
                                behavior: "smooth",
                                cancelable: false,
                            });
                        }, 1);
                    });
                },
            });
        },

        getDefaultEmailContent() {
            let content = `Please take a look at ticket <a href="${SITE_BASE}/tickets/${this.ticket.id}">${this.ticket.id}</a>.`;
            if (this.emailSignatureWithVariablesReplaced) {
                content = content + `<br />` + this.emailSignatureWithVariablesReplaced;
            }
            return content;
        },

        showCcField() {
            this.showCc = true;
        },

        showBccField() {
            this.showBcc = true;
        },

        openMediaUploader() {
            this.$bvModal.show("send-to-tc-upload-media-modal");
        },

        attemptPopulateTravelConsultant() {
            if (
                this.axumData
                && this.axumData.consultant
                && this.axumData.consultant.emailAlias
            ) {
                this.toAddresses = [this.axumData.consultant.emailAlias];
            }
        },

        handleFileUploaded(r, imgCommand) {
            this.loadMedia();
            let url = r["url"];
            this.$bvModal.hide("send-to-tc-upload-media-modal");
            if (url) {
                imgCommand({ src: url });
            }
        }
    }
}
</script>

<style lang="scss">
#send-to-tc-modal {
    .modal-body {
        padding: 0;
    }

    .modal-content {
        overflow: hidden;
    }

    .send-to-tc-card {
        margin-bottom: 0 !important;
        border-top: none !important;
        border-left: none !important;
        border-right: none !important;
        border-bottom: none !important;
    }
}
</style>